import React from 'react';
import { Node } from '@xyflow/react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAction } from 'convex/react';
import { api } from '../../../../../../convex/_generated/api';
import { useEffect, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface PropertySidebarProps {
  node: Node;
  updateNodeData: (nodeId: string, data: any) => void;
  onClose: () => void;
}

export function PropertySidebar({ node, updateNodeData, onClose }: PropertySidebarProps) {
  const { config } = node.data;
  
  const [connections, setConnections] = useState<any[]>([]);
  const listConnections = useAction(api.connections.list);

  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const result = await listConnections();
        if (result) setConnections(result);
      } catch (err) {
        console.error("Failed to fetch Composio connections", err);
      }
    };
    fetchConnections();
  }, [listConnections]);

  const gmailConnections = connections.filter(c => c.name.toLowerCase().includes('gmail') && c.active);

  let nodeTypeName = "Node Settings";
  if (node.type === 'triggerNode') nodeTypeName = "Event Trigger";
  else if (node.type === 'actionNode') nodeTypeName = "Action";
  else if (node.type === 'delayNode') nodeTypeName = "Delay";

  const triggerOptions = [
    {
      category: 'Bids',
      options: [
        { value: 'on_bid_created', label: 'When a bid is created' },
        { value: 'on_bid_submitted', label: 'When a bid is submitted' },
        { value: 'on_bid_approved', label: 'When a bid is approved' },
        { value: 'on_bid_rejected', label: 'When a bid is rejected' },
        { value: 'on_bid_task_created', label: 'When a bid task is created' },
        { value: 'on_bid_task_completed', label: 'When a bid task is completed' },
      ]
    },
    {
      category: 'Clients & Leads',
      options: [
        { value: 'on_lead_created', label: 'When a new lead is added' },
        { value: 'on_client_created', label: 'When a new client is added' },
        { value: 'on_client_converted', label: 'When a lead converts to a client' },
        { value: 'on_lead_task_created', label: 'When a lead task is created' },
        { value: 'on_lead_task_completed', label: 'When a lead task is completed' },
      ]
    },
    {
      category: 'Service Locations',
      options: [
        { value: 'on_location_created', label: 'When a service location is added' },
        { value: 'on_location_assigned', label: 'When a location is assigned to an employee' },
      ]
    },
    {
      category: 'Service Projects',
      options: [
        { value: 'on_project_created', label: 'When a project is created' },
        { value: 'on_project_completed', label: 'When a project is completed' },
      ]
    },
    {
      category: 'Location Onboarding',
      options: [
        { value: 'on_location_onboarding_started', label: 'When onboarding is started' },
        { value: 'on_location_onboarding_days_remaining', label: 'Days remaining threshold reached' },
        { value: 'on_location_onboarding_completed', label: 'When onboarding is completed' },
      ]
    },
    {
      category: 'Equipment',
      options: [
        { value: 'on_equipment_added', label: 'When equipment is added' },
        { value: 'on_equipment_assigned', label: 'When equipment is assigned' },
        { value: 'on_equipment_maintenance', label: 'When maintenance is logged' },
      ]
    },
    {
      category: 'Ratings & Reviews',
      options: [
        { value: 'on_review_created', label: 'When a new review is submitted' },
      ]
    },
    {
      category: 'Employees & Subcontractors',
      options: [
        { value: 'on_employee_created', label: 'When a new employee is onboarded' },
        { value: 'on_subcontractor_created', label: 'When a new subcontractor is onboarded' },
      ]
    }
  ];

  // Renders specific fields based on node type
  const renderNodeSettings = () => {
    switch (node.type) {
      case 'triggerNode':
        return (
          <div className="space-y-6 mt-6">
            <div className="space-y-4">
              <Label className="text-base">Event Type</Label>
              <div className="space-y-6">
                {triggerOptions.map(group => (
                  <div key={group.category} className="space-y-2">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{group.category}</div>
                    <div className="grid gap-2">
                      {group.options.map(option => {
                        const isSelected = config.eventType === option.value;
                        return (
                          <div 
                            key={option.value}
                            onClick={() => updateNodeData(node.id, { config: { ...config, eventType: option.value } })}
                            className={`p-3 rounded-md border text-sm cursor-pointer transition-colors ${
                              isSelected 
                                ? 'border-primary bg-primary/10 text-foreground font-medium' 
                                : 'border-border bg-card text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                            }`}
                          >
                            {option.label}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'actionNode':
        return (
          <div className="space-y-6 mt-6">
            <div className="space-y-2">
              <Label>Action Type</Label>
              <Select 
                value={config.actionType || ''} 
                onValueChange={(value) => updateNodeData(node.id, { config: { ...config, actionType: value } })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select action..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="updateRecord">Update Record</SelectItem>
                  <SelectItem value="sendEmail">Send Email</SelectItem>
                  <SelectItem value="createTask">Create Task</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {config.actionType === 'updateRecord' && (
              <>
                <div className="space-y-2">
                  <Label>Object Type</Label>
                  <Select 
                    value={config.objectType || ''} 
                    onValueChange={(newType) => {
                      let autoFillId = config.recordId;
                      if (newType === 'bid') autoFillId = '{{bid.id}}';
                      if (newType === 'business') autoFillId = '{{business.id}}';
                      if (newType === 'task') autoFillId = '{{task.id}}';
                      
                      updateNodeData(node.id, { config: { ...config, objectType: newType, recordId: autoFillId } });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select object..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="business">Business</SelectItem>
                      <SelectItem value="bid">Bid</SelectItem>
                      <SelectItem value="task">Task</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Record ID</Label>
                  <Input 
                    value={config.recordId || ''}
                    onChange={(e) => updateNodeData(node.id, { config: { ...config, recordId: e.target.value } })}
                    placeholder="e.g. {{bid.id}} or specific ID"
                  />
                </div>
              </>
            )}

            {config.actionType === 'sendEmail' && (
              <>
                <div className="space-y-2">
                  <Label>Send From</Label>
                  <Select 
                    value={config.provider || 'system'} 
                    onValueChange={(value) => updateNodeData(node.id, { config: { ...config, provider: value } })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="system">System Default (Resend)</SelectItem>
                      {gmailConnections.map(conn => (
                        <SelectItem key={conn.id} value={`composio_gmail:${conn.identity}`}>
                          My Gmail ({conn.description})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>To</Label>
                  <Input 
                    value={config.to || ''}
                    onChange={(e) => updateNodeData(node.id, { config: { ...config, to: e.target.value } })}
                    placeholder="e.g. {{client.email}}"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input 
                    value={config.subject || ''}
                    onChange={(e) => updateNodeData(node.id, { config: { ...config, subject: e.target.value } })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Body</Label>
                  <Textarea 
                    rows={6}
                    value={config.body || ''}
                    onChange={(e) => updateNodeData(node.id, { config: { ...config, body: e.target.value } })}
                  />
                </div>
              </>
            )}
          </div>
        );

      case 'delayNode':
        return (
          <div className="space-y-6 mt-6">
            <div className="flex gap-4">
              <div className="flex-1 space-y-2">
                <Label>Duration</Label>
                <Input 
                  type="number"
                  value={config.duration || ''}
                  onChange={(e) => updateNodeData(node.id, { config: { ...config, duration: e.target.value } })}
                />
              </div>
              <div className="flex-1 space-y-2">
                <Label>Unit</Label>
                <Select 
                  value={config.unit || 'days'} 
                  onValueChange={(value) => updateNodeData(node.id, { config: { ...config, unit: value } })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select unit..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minutes">Minutes</SelectItem>
                    <SelectItem value="hours">Hours</SelectItem>
                    <SelectItem value="days">Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );
      
      default:
        return <div className="mt-6 text-sm text-muted-foreground">No settings available for this node type.</div>;
    }
  };

  return (
    <aside className="w-[400px] bg-background border-l shadow-2xl flex flex-col absolute right-0 top-0 bottom-0 z-50 transition-all duration-300 ease-in-out">
      <div className="flex items-center justify-between p-6 border-b">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{nodeTypeName}</h3>
          <p className="text-sm text-muted-foreground">Configure properties for the selected node.</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-6">
          <div className="space-y-2">
            <Label>Node Label</Label>
            <Input 
              value={node.data.label as string || ''}
              onChange={(e) => updateNodeData(node.id, { label: e.target.value })}
            />
          </div>
          {renderNodeSettings()}
        </div>
      </ScrollArea>
    </aside>
  );
}
